import {default as parser, source as parserSource, version as parserVersion} from './grammar.pegjs';
import {default as chunks} from './chunks.pegjs';
import { sanitize } from './parse.js';
import { bindPrevObjs } from './bind_prev.js';
import { res, ui } from './stores.js';
import { get } from 'svelte/store';
import { createStore, getMany, setMany, keys as idbKeys, delMany } from 'idb-keyval';

// Parse results persist across loads. A result depends only on the parser and the exact text and
// options it was given, so that is the key: a grammar change (new parserVersion) or an edited card
// misses, everything else skips the worker. Stored unbound, as the worker returned them, so a
// change to bind_prev.js never meets an already-bound tree.
const parseCache = createStore('stcc-parse-cache', 'results');
const CACHE_FORMAT = 1; // bump if what the worker stores per card changes (parserVersion only covers the grammar)
const cacheVersion = `${CACHE_FORMAT}.${parserVersion}`;
const cacheKey = c => `${cacheVersion}|${c.botCard}|${c.stardate}|${c.sanitized}`;
let prunedOldVersions = false;

async function readParseCache(toParse){
    let ids = Object.keys(toParse);
    try{
        // Cards with the same text share one result, as they do coming out of the worker
        let unique = [...new Set(ids.map(id => cacheKey(toParse[id])))];
        let found = await getMany(unique, parseCache);
        let byKey = new Map(unique.map((k, i) => [k, found[i]]));
        let hits = {};
        for(let id of ids){
            let hit = byKey.get(cacheKey(toParse[id]));
            // origText is the card's own (sanitize() can map different texts to one key)
            if(hit) hits[id] = {...hit, origText: toParse[id].origText};
        }
        return hits;
    }catch(e){
        console.warn('[parse] cache unavailable', e);
        return {};
    }
}

function writeParseCache(toParse, parsed){
    try{
        // cloned now: setMany stores asynchronously, after these results have been bound
        let entries = Object.keys(parsed).map(id => [cacheKey(toParse[id]), structuredClone(parsed[id])]);
        setMany(entries, parseCache).catch(e => console.warn('[parse] cache write failed', e));
        if(!prunedOldVersions){
            prunedOldVersions = true;
            idbKeys(parseCache)
                .then(all => delMany(all.filter(k => !String(k).startsWith(cacheVersion + '|')), parseCache))
                .catch(e => console.warn('[parse] cache prune failed', e));
        }
    }catch(e){
        console.warn('[parse] cache write failed', e);
    }
}

// Shared by pegParse and the worker (which gets it via toString, so it must stay self-contained)
function describeError(error){
    return {
        error: error.message || String(error),
        place: error.location ? error.location.start.offset : undefined
    };
}

function createWorkerBundle(cardObj){
    // The worker evaluates the same generated parser source as the main thread, so it has the
    // real SyntaxError class (and its error messages) rather than a copy of parse() alone.
    let blobURL = URL.createObjectURL(new Blob([`
    const parser = ${parserSource};
    const describeError = ${describeError.toString()};

    self.onmessage = function (e){
        let arg = e.data;
        let parse = parser.parse;
        let done = {};
        let byCardText = {};
        let progPerCard = 100 / Object.keys(arg).length;
        let progress = 0;
        let lastPosted = 0;
        for(let k of Object.keys(arg)){
            let before = performance.now();
            let memoKey = arg[k].origText + '|' + arg[k].botCard + '|' + arg[k].stardate;
            if(byCardText[memoKey]){
                // memoize
                done[k] = byCardText[memoKey];
            }else{
                try{
                
                    let response = parse(arg[k].sanitized, {botCard: arg[k].botCard, stardate: arg[k].stardate, inApp: true});
                    let after = performance.now();
                    let timeTaken = after - before;
                    //console.log(response)
                    done[k] = {
                        text: arg[k].sanitized,
                        origText: arg[k].origText,
                        effects: response.map(r => { return {...r, name: r.verb || r.type}}),
                        timeTaken,
                        response
                    }
                    byCardText[memoKey] = {...done[k]};
                }catch(e){
                    console.log(e);
                    let after = performance.now();
                    done[k] = {
                        text: arg[k].sanitized,
                        origText: arg[k].origText,
                        response: 'error',
                        timeTaken: after - before,
                        ...describeError(e)
                    }
                }
            }
            
            progress += progPerCard;
            // one message per whole percent, not per card – each one re-renders the main thread
            if(progress - lastPosted >= 1){
                lastPosted = progress;
                postMessage({
                    type: 'progress',
                    progress
                })
            }
        }
        postMessage({
            type: 'done',
            result: done
        });
    }`], {type: 'application/javascript'}));

    let worker = new Worker(blobURL);

    URL.revokeObjectURL(blobURL);
    worker.postMessage(cardObj);
    return worker;
}

function parseInWorker(cardObj){
    return new Promise(resolve => {
        let work = createWorkerBundle(cardObj);
        work.onmessage = (e) => {
            if(e.data.type == 'done'){
                work.terminate(); // each holds its own copy of the parser
                resolve(e.data.result);
            }else if(e.data.type == 'progress'){
                ui.update(ui => {
                    ui.loadingProgress = e.data.progress;
                    return ui; 
                })
            }
        }
    })
}

export async function parseCards(cards, temp = false){ // temp = just for this game
    
    let notParsing = [];

    let pct = get(res).parsedCardText;
    
    if(pct){
        // Only parse changed cards
        //console.log('filtering out cached cards', pct)
        cards = cards.filter(c => {
            if(!c) return false;
            //if(!c.cacheInfo) return true;
            //console.log('has cacheinfo', c.cacheInfo)
            let cachedPCT = pct[c.id];
            if(!cachedPCT){
                // console.log('no cache', c.id)
                return true;
            }
            //console.log('has cachedPCT', cachedPCT)
            if(c.allText != cachedPCT.origText){
                console.log('different text', c.allText, cachedPCT.origText, c, cachedPCT);
                return true;
            }
            //console.log('same text', cachedPCT)
            if(temp || get(ui).reloadParser){
                return true;
            }
            //console.log('No need to parse', c, cachedPCT);
            bindPrevObjs(cachedPCT.response, c); // saves predating the binder arrive unbound
            notParsing.push(c)
            return false;
        });
    }
    console.log(cards.length+' cards to parse – not parsing '+notParsing.length+' as no change')
    let wallStart = performance.now();
    let toParse = {};
    for(let c of cards){
        toParse[c.id] = {
            origText: c.allText,
            sanitized: sanitize(c.allText.trim()),
            botCard: c.botCard,
            stardate: c.stardate
        };
    }
    let order = Object.keys(toParse);
    let fromCache = get(ui).reloadParser ? {} : await readParseCache(toParse);
    for(let id of Object.keys(fromCache)) delete toParse[id];

    let parsed = {};
    if(Object.keys(toParse).length){
        parsed = await parseInWorker(toParse);
        writeParseCache(toParse, parsed);
    }

    let byId = new Map(cards.map(c => [c.id, c]));
    res.update(r => {
        for(let id of order){
            let result = fromCache[id] || parsed[id];
            bindPrevObjs(result.response, byId.get(id)); // effects share these nodes, so both get bound
            r.parsedCardText[id] = result;
        }
        return r;
    });
    let timerResults = Object.values(parsed).map(p => ({text: p.origText, timeTaken: p.timeTaken}))
        .sort((a,b) => b.timeTaken - a.timeTaken);
    let parseTotal = timerResults.reduce((a,b) => a + b.timeTaken, 0);
    let wall = performance.now() - wallStart;
    console.log(`[parse] ${Object.keys(fromCache).length} from cache, ${timerResults.length} parsed: ${Math.round(parseTotal)}ms parsing / ${Math.round(wall)}ms wall (${Math.round(wall - parseTotal)}ms overhead)`);
    if(timerResults.length) console.log(`[parse] times:`, timerResults);
}

export function pegParse(text, options = {}){
    try {
        //console.log(parser)
        let response = parser.parse(text, options);
        //console.log(response)
        bindPrevObjs(response, options.card || null);
        return {
            text,
            effects: response.map(r => { return {...r, name: r.verb || r.type}}),
            response
        }
    }catch(error){
        return {
            text,
            response: 'error',
            ...describeError(error)
        }
    }
}

export function parseChunks(text){
    try {
        let response = chunks.parse(text);
        //console.log(response)
        return {
            text,
            effects: response.map(r => { return {...r, name: r.verb || r.type}}),
            response
        }
    }catch(error){
        return {
            text,
            response: 'error',
            ...describeError(error)
        }
    }
}
